import type { ZodType } from 'zod';
import { z } from 'zod';
import { OPERATION } from './enums';

// Helper function to check if a value is a Zod schema using duck typing
const isZodSchema = (val: unknown): val is ZodType => {
  return (
    val !== null &&
    typeof val === 'object' &&
    'safeParse' in val &&
    typeof val.safeParse === 'function' &&
    '_def' in val
  );
};

export const PrimitiveSchema = z.union([z.number(), z.string(), z.boolean()]);

export const PrimitiveArraySchema = z.array(z.union([z.number(), z.string(), z.boolean()]));

export const BetweenSchema = z.object({
  min: PrimitiveSchema,
  max: PrimitiveSchema,
});

export const LastYearsSchema = z.object({
  years: z.number().positive(),
});

export const ExistsSchema = z.object({
  schema: z.any().refine(isZodSchema, {
    message: 'Value must be a Zod schema',
  }),
});

export const BaseOperationsValueSchema = z.union([
  z.object({
    operator: z.literal(OPERATION.EQUALS),
    value: PrimitiveSchema,
  }),
  z.object({
    operator: z.literal(OPERATION.NOT_EQUALS),
    value: PrimitiveSchema,
  }),
  z.object({
    operator: z.literal(OPERATION.BETWEEN),
    value: BetweenSchema,
  }),
  // Add other operator-specific schemas here
  z.object({
    operator: z.literal(OPERATION.GT),
    value: PrimitiveSchema,
  }),
  z.object({
    operator: z.literal(OPERATION.LT),
    value: PrimitiveSchema,
  }),
  z.object({
    operator: z.literal(OPERATION.GTE),
    value: PrimitiveSchema,
  }),
  z.object({
    operator: z.literal(OPERATION.LTE),
    value: PrimitiveSchema,
  }),
  z.object({
    operator: z.literal(OPERATION.IN),
    value: PrimitiveArraySchema,
  }),
  z.object({
    operator: z.literal(OPERATION.NOT_IN),
    value: PrimitiveArraySchema,
  }),
]);

export const AmlCheckSchema = z
  .object({
    childWorkflowName: z.string(),
  })
  .and(BaseOperationsValueSchema);

export const AmlCheckV2Schema = BaseOperationsValueSchema;

export const BankAccountVerificationSchema = z.object({
  operator: z.literal(OPERATION.BANK_ACCOUNT_VERIFICATION),
});

export const IdvCheckSchema = z.object({
  childWorkflowName: z.string(),
});

export const CompanySanctionsCategoriesSchema = z.object({
  threshold: z.number().optional(),
  category: z.union([z.literal('Adverse Media'), z.string()]),
});

export const UboMismatchSchema = z.object({
  operator: z.literal(OPERATION.UBO_MISMATCH),
});
